import React, { useState, useCallback, useEffect, useRef } from 'react';
import { LocationProvider } from './context/LocationContext';
import { ImpersonationProvider, useImpersonation } from './context/ImpersonationContext';
import Layout from './components/Layout';
import StaffRecommend from './StaffRecommend';
import { parseLogHash, clearLogHash } from './lib/logDeepLink';

// Role → module access (same as StaffRecommend, needed by Layout for nav visibility)
const ROLE_MODULES = {
  owner:            ['tasks', 'hr', 'performance', 'guests', 'analytics', 'kiosk', 'reporting', 'poppy_reports', 'poppy_book', 'poppy_master_data', 'inventory', 'sync', 'calloff', 'campaigns', 'month_end'],
  ecr_manager:      ['tasks', 'hr', 'performance', 'guests', 'analytics', 'kiosk', 'reporting', 'poppy_reports', 'poppy_book', 'poppy_master_data', 'inventory', 'sync', 'calloff'],
  salon_coordinator:['tasks', 'performance', 'guests', 'analytics', 'kiosk', 'reporting', 'inventory', 'calloff'],
  lead_waxer:       ['schedule', 'tasks', 'performance', 'reporting', 'inventory', 'me'],
  lead_nail_tech:   ['schedule', 'tasks', 'performance', 'reporting', 'inventory', 'me'],
  receptionist:     ['tasks', 'reporting'],
  esthetician:      ['schedule', 'tasks', 'me'],
  nail_tech:        ['schedule', 'tasks', 'me'],
  employee:         ['schedule', 'tasks', 'me'],
};

function AppInner() {
  const [session, setSession] = useState(null);
  // Service-log reminder deep link (#log/<apptId>/<serviceId>): land on the
  // schedule view and let StaffRecommend drive the rest once it has data.
  const [logDeepLink, setLogDeepLink] = useState(() => parseLogHash(window.location.hash));
  const [staffView, setStaffView] = useState(() => {
    if (window.location.hash === '#pkgsales') return 'kiosk'; // seller push + morning alert land on Kiosk Analytics
    if (window.location.hash === '#change-requests-log') return 'change_requests'; // DeskNotes "Full log in ops →" link
    return parseLogHash(window.location.hash) ? 'clients' : 'tasks';
  });
  const signOutRef = useRef(null);
  const { impersonatedRole } = useImpersonation();

  const handleSessionChange = useCallback((sess) => {
    setSession(sess);
  }, []);

  // The SW's notificationclick navigates an already-open app to the #log url;
  // that reloads the document in most browsers, but listen for hashchange as
  // the fallback path (e.g. a second notification tapped while open).
  useEffect(() => {
    const onHash = () => {
      if (window.location.hash === '#pkgsales') { setStaffView('kiosk'); return; }
      if (window.location.hash === '#change-requests-log') { setStaffView('change_requests'); return; }
      const target = parseLogHash(window.location.hash);
      if (target) { setLogDeepLink(target); setStaffView('clients'); }
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const consumeLogDeepLink = useCallback(() => { setLogDeepLink(null); clearLogHash(); }, []);

  // Sidebar visibility uses the impersonated role when active so the nav
  // mirrors the staff member's view. Real opsRole is unchanged elsewhere
  // (auth, write gates, the View As button itself).
  const hasModule = useCallback((mod) => {
    const role = impersonatedRole || session?.opsRole || 'employee';
    return (ROLE_MODULES[role] || ROLE_MODULES.employee).includes(mod);
  }, [session, impersonatedRole]);

  return (
    <LocationProvider>
      <Layout
        session={session}
        onSignOut={() => { if (signOutRef.current) signOutRef.current(); }}
        activeView={staffView}
        onNavigate={setStaffView}
        hasModule={hasModule}
      >
        <StaffRecommend
          staffView={staffView}
          setStaffView={setStaffView}
          onSessionChange={handleSessionChange}
          onLayoutSignOut={null}
          signOutRef={signOutRef}
          logDeepLink={logDeepLink}
          onLogDeepLinkConsumed={consumeLogDeepLink}
        />
      </Layout>
    </LocationProvider>
  );
}

function App() {
  return (
    <ImpersonationProvider>
      <AppInner />
    </ImpersonationProvider>
  );
}

export default App;
